import { computed } from 'vue';
import { type RouteLocationNormalized, type RouteLocationRaw, useRouter } from 'vue-router';

import { useCartStore } from '@/stores/cart';
import { useCheckoutStore } from '@/stores/checkoutStore';
import { closeEmbedDrawer } from '@/utils/embedNavigation';
import { slugify } from '@/utils/slugify';

export type CheckoutStepName =
  | 'checkout-tickets'
  | 'checkout-upsale'
  | 'checkout-seats'
  | 'checkout-information'
  | 'checkout-payment'
  | 'checkout-receipt';

const CHECKOUT_STEPS: CheckoutStepName[] = [
  'checkout-tickets',
  'checkout-upsale',
  'checkout-seats',
  'checkout-information',
  'checkout-payment',
  'checkout-receipt',
];

const STATIC_STEP_PATHS: Partial<Record<CheckoutStepName, string>> = {
  'checkout-upsale': '/checkout/upsale',
  'checkout-seats': '/checkout/seats',
  'checkout-information': '/checkout/information',
  'checkout-payment': '/checkout/payment',
  'checkout-receipt': '/checkout/receipt',
};

export function useCheckoutNavigation() {
  const router = useRouter();
  const cart = useCartStore();
  const checkoutStore = useCheckoutStore();

  function getStepPath(step: CheckoutStepName): RouteLocationRaw {
    if (step === 'checkout-tickets') {
      const eventId = checkoutStore.getCheckoutID();
      const isClosedEvent = typeof eventId === 'string' && /^[0-9a-f]{96}$/.test(eventId);
      if (isClosedEvent) {
        return { name: 'checkout-tickets', params: { id: eventId } };
      }
      let eventName = 'event';
      if (eventId) {
        const cartItem = cart.items.find(item => item.id == eventId);
        if (cartItem?.name) {
          eventName = slugify(cartItem.name);
        } else if (checkoutStore.event?.info1) {
          eventName = slugify(checkoutStore.event.info1);
        }
      }
      return { name: 'checkout-tickets', params: { id: eventId ?? 0, name: eventName } };
    }
    return STATIC_STEP_PATHS[step]!;
  }

  function isStepActive(step: CheckoutStepName): boolean {
    switch (step) {
      case 'checkout-upsale': {
        const available = cart.getAvailableUpsaleTicketsForBasket();
        return checkoutStore.upsaleTickets.some(t => available.includes(Number(t.id)));
      }
      case 'checkout-seats':
        return cart.seatmapActive;
      default:
        return true;
    }
  }

  const activeSteps = computed(() =>
    CHECKOUT_STEPS.filter(step => isStepActive(step))
  );

  function getNextRoute(currentRouteName: string): RouteLocationRaw | null {
    const currentIndex = activeSteps.value.indexOf(currentRouteName as CheckoutStepName);
    if (currentIndex === -1 || currentIndex === activeSteps.value.length - 1) return null;
    return getStepPath(activeSteps.value[currentIndex + 1]);
  }

  function getPreviousRoute(currentRouteName: string): RouteLocationRaw | null {
    const currentIndex = activeSteps.value.indexOf(currentRouteName as CheckoutStepName);
    if (currentIndex <= 0) return null;
    return getStepPath(activeSteps.value[currentIndex - 1]);
  }

  function navigateToNext(currentRouteName: string) {
    const next = getNextRoute(currentRouteName);
    if (next) {
      router.push(next);
    }
  }

  function navigateToPrevious(currentRouteName: string) {
    const previous = getPreviousRoute(currentRouteName);
    if (previous) {
      router.push(previous);
    }
  }

  function navigateBack(currentRouteName: string) {
    if (currentRouteName === 'checkout-tickets') {
      if (closeEmbedDrawer()) return;
      const eventId = checkoutStore.getCheckoutID();
      if (eventId) {
        router.push({ name: 'event-detail', params: { id: eventId } });
      } else {
        router.push('/');
      }
      return;
    }
    const previous = getPreviousRoute(currentRouteName);
    if (previous) {
      router.push(previous);
    } else {
      router.back();
    }
  }

  function navigateToReceipt(rtoken: string) {
    router.push('/receipt?rtoken=' + rtoken);
  }

  /**
   * Determines whether navigation away from a checkout route should be blocked
   * to show the exit confirmation modal.
   *
   * Returns 'exit' if the exit modal should be shown,
   * 'cancel-payment' if the cancel-payment modal should be shown,
   * or false if navigation should proceed.
   *
   * NOTE: The exit modal is currently disabled (always returns false for exit scenarios).
   * To re-enable, remove the early return marked below.
   */
  function shouldBlockNavigation(
    to: RouteLocationNormalized,
    from: RouteLocationNormalized,
  ): 'exit' | 'cancel-payment' | false {
    const fromName = from.name?.toString() ?? '';
    const toName = to.name?.toString() ?? '';

    // ── Cancel-payment guard ──────────────────────────────────────────────
    if (fromName.includes('checkout-payment') || fromName.includes('paymentfailed')) {
      if (
        cart.paymentFailedTimeoutFlag ||
        cart.ignoreCancelPaymentModal ||
        checkoutStore.paymentBegun ||
        checkoutStore.bypassPaymentGuard ||
        checkoutStore.ivpMode
      ) {
        checkoutStore.bypassPaymentGuard = false;
        cart.ignoreCancelPaymentModal = false;
        return false;
      }
      return 'cancel-payment';
    }

    // ── Exit guard (for tickets, upsale, information) ─────────────────────
    if (
      !cart.items.length ||
      to.query['prevent-guard'] ||
      checkoutStore.paymentBegun ||
      cart.isCartEmpty()
    ) {
      return false;
    }

    // disabled for now — remove these two lines to re-enable exit modal
    return false;

    if (fromName.includes('checkout') && !toName.includes('checkout')) {
      return 'exit';
    }

    return false;
  }

  return {
    activeSteps,
    isStepActive,
    getNextRoute,
    getPreviousRoute,
    getStepPath,
    navigateToNext,
    navigateToPrevious,
    navigateBack,
    navigateToReceipt,
    shouldBlockNavigation,
    CHECKOUT_STEPS,
    STATIC_STEP_PATHS,
  };
}
